// KanbanView — 4 columns (À traiter / Bloqué / À valider / Résolu) matching
// legacy stbarts DOM (index.html L1772-1815). Cards are draggable to change
// status (PATCH /api/v1/remarks/:id) and clicking a card opens the
// ContextOverlayPopup (Bug 3+4 fix — was a side-rail in alpha.10).

import type { Remark, RemarkStatus } from '@ladd-screenshot/shared';
import { For, Show, createMemo, createSignal } from 'solid-js';
import { actions, stores } from '../state/store';

interface RemarkWithCounts extends Remark {
  commentCount?: number;
  historyCount?: number;
}

interface KanbanColumn {
  key: 'open' | 'blocked' | 'pending' | 'resolved';
  label: string;
  statuses: RemarkStatus[];
  dropStatus: RemarkStatus;
}

const COLUMNS: KanbanColumn[] = [
  { key: 'open', label: 'À traiter', statuses: ['open', 'todo'], dropStatus: 'open' },
  { key: 'blocked', label: 'Bloqué', statuses: ['blocked', 'wontfix'], dropStatus: 'blocked' },
  { key: 'pending', label: 'À valider', statuses: ['pending', 'doing'], dropStatus: 'pending' },
  { key: 'resolved', label: 'Résolu', statuses: ['resolved', 'done'], dropStatus: 'resolved' },
];

// Short FR date "13 janv." — matches legacy renderKanbanCard L2791:
// `new Date(remark.createdAt).toLocaleDateString('fr-FR', { day, month: 'short' })`.
function dateShortFR(at: number): string {
  return new Date(at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
}

function authorLabel(remark: Remark): string {
  return remark.author.split('@')[0] ?? remark.author;
}

// Hash-based author color — same algorithm as legacy getAuthorColor()
// (index.html L2213-2230) so the same author keeps the same badge color
// across legacy and alpha.
const AUTHOR_COLORS = [
  '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7',
  '#dfe6e9', '#a29bfe', '#fd79a8', '#00b894', '#e17055',
  '#0984e3', '#6c5ce7', '#fdcb6e', '#e84393', '#00cec9',
];
const authorColorCache: Record<string, string> = {};
function getAuthorColor(author: string): string {
  if (!authorColorCache[author]) {
    let hash = 0;
    for (let i = 0; i < author.length; i++) {
      hash = author.charCodeAt(i) + ((hash << 5) - hash);
    }
    authorColorCache[author] = AUTHOR_COLORS[Math.abs(hash) % AUTHOR_COLORS.length]!;
  }
  return authorColorCache[author]!;
}

// Legacy stbarts target labels matching TARGET_PATHS in index.html L2176.
// Lowercase to match legacy rendering ("/en", "menu", not "/EN", "MENU").
const KANBAN_TARGET_LABEL: Record<string, string> = {
  'front:menu':                       'menu',
  'front:footer':                     'footer',
  'front:homepage':                   '/en',
  'front:villas':                     '/en/stay/villas',
  'front:villas-slug':                '/en/stay/villas/[slug]',
  'front:villas-slug-photos':         '/en/stay/villas/[slug]/photos',
  'front:villas-slug-photos-zoomed':  '/en/stay/villas/[slug]/photos (zoomed)',
  'front:villas-locations':           '/en/stay/villas/locations',
  'front:villas-locations-slug':      '/en/stay/villas/locations/[slug]',
  'front:villas-collections':         '/en/stay/villas/collections',
  'front:villas-collections-slug':    '/en/stay/villas/collections/[slug]',
};
function targetCardLabel(slug: string): string {
  return KANBAN_TARGET_LABEL[slug] ?? slug.replace(/^front:/, '');
}

export function KanbanView() {
  const [dragTarget, setDragTarget] = createSignal<string | null>(null);

  function handleDragStart(remarkId: string, e: DragEvent) {
    if (!e.dataTransfer) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', remarkId);
  }
  function handleDragOver(colKey: string, e: DragEvent) {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    setDragTarget(colKey);
  }
  function handleDragLeave(colKey: string) {
    if (dragTarget() === colKey) setDragTarget(null);
  }
  async function handleDrop(col: KanbanColumn, e: DragEvent) {
    e.preventDefault();
    setDragTarget(null);
    const remarkId = e.dataTransfer?.getData('text/plain');
    if (!remarkId) return;
    const remark = stores.state.remarks.find((r) => r.id === remarkId);
    if (!remark || col.statuses.includes(remark.status)) return;
    try {
      await actions.updateRemark(remarkId, { status: col.dropStatus });
    } catch (err) {
      console.warn('[kanban] updateRemark failed', err);
    }
  }

  return (
    <div class="kanban-view active" id="kanbanView">
      <For each={COLUMNS}>
        {(col) => {
          const matches = createMemo(() =>
            stores.state.remarks.filter((r) => col.statuses.includes(r.status)),
          );
          return (
            <div
              class={`kanban-column ${col.key} ${dragTarget() === col.key ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(col.key, e)}
              onDragLeave={() => handleDragLeave(col.key)}
              onDrop={(e) => void handleDrop(col, e)}
            >
              <div class="kanban-column-header">
                <h3>{col.label}</h3>
                <span class="count">{matches().length}</span>
              </div>
              <div class="kanban-cards">
                <Show
                  when={matches().length > 0}
                  fallback={<div class="kanban-empty">No remarks</div>}
                >
                  <For each={matches()}>
                    {(remark, idx) => {
                      const r = remark as RemarkWithCounts;
                      const isGlobal = r.resolutionName === 'global';
                      async function gotoTarget(e: MouseEvent) {
                        e.stopPropagation();
                        const t = stores.state.targets.find(
                          (x) => x.slug === remark.targetSlug,
                        );
                        if (!t) return;
                        await actions.selectTarget(t.id);
                        if (remark.resolutionName && remark.resolutionName !== 'global') {
                          actions.setActiveResolution(remark.resolutionName);
                        }
                        actions.setViewMode('last');
                        actions.openRemarkDetail(remark.id);
                      }
                      return (
                        <div
                          class="kanban-card"
                          draggable={true}
                          onDragStart={(e) => handleDragStart(remark.id, e)}
                          onDragEnd={() => setDragTarget(null)}
                          onClick={() => actions.openRemarkDetail(remark.id)}
                        >
                          <div class="kanban-card-header">
                            <span class="kanban-card-number">{idx() + 1}</span>
                            <span class="kanban-card-target" title={remark.targetSlug}>
                              {targetCardLabel(remark.targetSlug)}
                            </span>
                            <span class="kanban-card-resolution">
                              {isGlobal ? '🌐 Global' : (remark.resolutionName ?? '')}
                            </span>
                          </div>
                          <div class="kanban-card-text">{remark.text}</div>
                          <div class="kanban-card-footer">
                            <div class="kanban-card-author">
                              <span
                                class="author-badge"
                                style={{ background: getAuthorColor(remark.author) }}
                              >
                                {authorLabel(remark)}
                              </span>
                              <span title={new Date(remark.createdAt).toISOString()}>
                                {dateShortFR(remark.createdAt)}
                              </span>
                            </div>
                            <div class="kanban-card-actions">
                              <Show when={(r.commentCount ?? 0) > 0}>
                                <span class="kanban-card-comments">💬 {r.commentCount}</span>
                              </Show>
                              <Show when={!isGlobal}>
                                <button
                                  type="button"
                                  class="kanban-card-goto"
                                  title="Aller à la page"
                                  onClick={(e) => void gotoTarget(e)}
                                >
                                  🎯
                                </button>
                              </Show>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </div>
            </div>
          );
        }}
      </For>
    </div>
  );
}
